/**
 * Dynamic Discount Price Display
 * Applies configured discounts to standard price elements across the site
 *
 * Scope: Handles [data-dynamic-discount-price] elements in:
 * - price.liquid snippet
 * - custom-price.liquid snippet
 * - Product cards, quick-add modals, recommendations
 *
 * SKIO Integration: Separated to custom-dynamic-discount-skio.js
 * - SKIO displays its own prices (no modifications)
 * - Discount codes apply correctly at cart/checkout
 */

if (typeof window.DynamicDiscountPrice === 'undefined') {
  window.DynamicDiscountPrice = class {
    constructor(discountConfig) {
      this.discountConfig = discountConfig || {};
      this.init();
    }

    init() {
      const discountCode = this.getCookie('discount_code');
      if (!discountCode) return;

      const discount = this.discountConfig[discountCode];
      if (!discount || !this.isDiscountValidForCustomer(discount)) return;

      this.applyDiscount(discountCode, discount);
      this.watchForDynamicContent(discountCode, discount);
      this.watchForVariantChanges(discountCode, discount);
    }

    watchForVariantChanges(discountCode, discount) {
      document.addEventListener('variant:update', (event) => {
        setTimeout(() => {
          // Reset all discount flags
          document.querySelectorAll('[data-discount-applied="true"]').forEach(el => {
            el.removeAttribute('data-discount-applied');
          });

          // Re-apply discounts to standard price elements
          this.applyDiscount(discountCode, discount);
        }, 100);
      });
    }

    watchForDynamicContent(discountCode, discount) {
      const recommendationElements = document.querySelectorAll('product-recommendations');

      recommendationElements.forEach(element => {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' &&
                mutation.attributeName === 'data-recommendations-performed' &&
                element.dataset.recommendationsPerformed === 'true') {
              this.applyDiscount(discountCode, discount);
            }
          });
        });

        observer.observe(element, {
          attributes: true,
          attributeFilter: ['data-recommendations-performed']
        });
      });

      const quickAddModal = document.getElementById('quick-add-modal-content');
      if (quickAddModal) {
        const modalObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              setTimeout(() => {
                this.applyDiscount(discountCode, discount);
              }, 100);
            }
          });
        });

        modalObserver.observe(quickAddModal, {
          childList: true,
          subtree: false
        });
      }
    }

    isDiscountValidForCustomer(discount) {
      const isB2B = window.IS_B2B_CUSTOMER || false;
      if (discount.retailOnly && isB2B) return false;
      if (discount.wholesaleOnly && !isB2B) return false;
      return true;
    }

    isDiscountValidForVariant(variantId) {
      const filter = window.VARIANT_FILTER || { mode: 'sitewide', variantIds: [] };

      if (filter.mode === 'sitewide') return true;

      const variantIdStr = String(variantId);
      const isInList = filter.variantIds.includes(variantIdStr);

      if (filter.mode === 'include_only') {
        return isInList;
      } else if (filter.mode === 'exclude') {
        return !isInList;
      }

      return true;
    }

    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    }

    applyDiscount(code, discount) {
      const priceContainers = document.querySelectorAll('[data-dynamic-discount-price]');

      priceContainers.forEach(container => {
        const variantId = container.dataset.variantId;

        // Check if this variant passes the filter
        if (!this.isDiscountValidForVariant(variantId)) return;

        const price = parseInt(container.dataset.price);
        const compareAtPrice = parseInt(container.dataset.compareAt) || null;

        let discountedPrice;
        if (discount.type === 'percentage') {
          discountedPrice = Math.round(price * (100 - discount.value) / 100);
        } else {
          discountedPrice = Math.max(0, price - discount.value);
        }

        if (discountedPrice < price) {
          this.updatePriceDisplay(container, price, discountedPrice, compareAtPrice);
        }
      });
    }

    updatePriceDisplay(container, originalPrice, discountedPrice, compareAtPrice) {
      const formattedDiscounted = this.formatMoney(discountedPrice);
      const comparePrice = compareAtPrice || originalPrice;
      const formattedCompare = this.formatMoney(comparePrice);

      const priceElement = container.querySelector('.price');
      if (!priceElement) return;

      priceElement.textContent = formattedDiscounted;

      let compareElement = container.querySelector('.compare-at-price');

      if (compareElement) {
        compareElement.textContent = formattedCompare;
        if (compareElement.parentElement) {
          compareElement.parentElement.style.display = '';
        }
      } else {
        const newCompareElement = document.createElement('span');
        newCompareElement.className = 'compare-at-price';
        newCompareElement.textContent = formattedCompare;
        newCompareElement.style.marginLeft = '0.5em';
        priceElement.insertAdjacentElement('afterend', newCompareElement);
      }

      container.dataset.discountApplied = 'true';
    }

    formatMoney(cents) {
      const dollars = Math.round(cents / 100);

      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
      }

      return `$${dollars}`;
    }
  };

  window.initDynamicDiscount = function(config, isB2B) {
    if (window.dynamicDiscountInitialized) return;
    window.dynamicDiscountInitialized = true;

    const discountConfig = config || window.DISCOUNT_CONFIG || {};

    if (typeof isB2B !== 'undefined') {
      window.IS_B2B_CUSTOMER = isB2B;
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        new window.DynamicDiscountPrice(discountConfig);
      });
    } else {
      new window.DynamicDiscountPrice(discountConfig);
    }
  };

  if (window.DISCOUNT_CONFIG) {
    window.initDynamicDiscount();
  }
}
